import { FC, MouseEventHandler, useState } from 'react';
import { AnalyticsContext, Button as IButton, Maybe } from '@/types/generated';
import { DynamicFormConfig } from '../DynamicForm/types/DynamicFormTypes';
import Quiz from '../Quiz';
import { buttonStyles } from './ContentfulButton.styles';
import { triggerCrestaChat, useCrestaAgent } from './useCrestaAgent';
import { TFormData, quizGA4DataLayerPush } from '../Quiz/Quiz.helpers';
import TAGButton, { type ITAGButton } from '@shared-components/components/TAGButton';
import TAGSvgIcon from '@shared-components/components/TAGSvgIcon';
import { useAppContext, useFacilityContext, useOfficeLocationContext } from '@shared-components/context';
import { ContentfulButtonSize, ContentfulButtonVariant } from '@shared-components/types';
import { normalizeDomainDisplayLink, resolveContentfulButtonSize, resolveContentfulButtonVariant, resolveContentfulResponsiveButton, ButtonIconSizes } from '@utils';
import { replaceContentVars } from '@utils/replaceContentVars';
import { resolveEntryLink } from '@utils/resolveButtonEntryLink';
interface IContentfulButton extends IButton {
  isSmallScreen?: boolean;
  dataTestId?: string;
  defaultVariant?: Exclude<ITAGButton['variant'], undefined>;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  fullWidth?: boolean;
  analyticsContext?: Maybe<AnalyticsContext>;
  ariaLabel?: string;
  docProps?: Record<string, unknown>;
}
const openLinkIn = (link: string, typeOfOpening: 'same_tab' | 'new_tab' | 'new_window') => {
  const openInNewContext = (features?: string) => {
    const featureList = features ? `${features},noopener,noreferrer` : 'noopener,noreferrer';
    const newWindow = globalThis.window.open('', '_blank', featureList);
    if (newWindow) {
      newWindow.opener = null;
      newWindow.location.href = link;
    }
  };
  if (typeOfOpening === 'new_tab') {
    openInNewContext();
  } else if (typeOfOpening === 'new_window') {
    openInNewContext('width=600,height=400');
  } else {
    globalThis.location.href = link;
  }
};
const getTypeOfOpening = (openInNewWindow?: boolean | null, openInNewTab?: boolean | null): 'new_window' | 'new_tab' | 'same_tab' => {
  if (openInNewWindow) return 'new_window';
  if (openInNewTab) return 'new_tab';
  return 'same_tab';
};
export const buttonIconSize = (size?: Maybe<ButtonIconSizes>): number => {
  const sizes = {
    XSmall: 12,
    Small: 16,
    Medium: 25,
    Large: 32,
    XLarge: 38
  } as {
    [key: string]: number;
  };
  return sizes[size || 'Medium'];
};
const ContentfulButton: FC<IContentfulButton> = ({
  defaultVariant,
  dataTestId = 'tag_button',
  isSmallScreen = false,
  onClick = () => undefined,
  fullWidth = false,
  quizDetails,
  ariaLabel,
  docProps = {},
  popoverContentCollection,
  launchCrestaAgent,
  ...buttonData
}) => {
  const [state, setState] = useState<{
    showQuiz: boolean;
  }>({
    showQuiz: false
  });
  const {
    ready: crestaReady
  } = useCrestaAgent(!!launchCrestaAgent);
  const {
    config
  } = useAppContext();
  const {
    officeInfo
  } = useFacilityContext();
  const {
    officeInfo: officeLocationInfo
  } = useOfficeLocationContext();
  if (!buttonData) return null;
  const button = resolveContentfulResponsiveButton(buttonData, isSmallScreen);
  const resolvedLink = resolveEntryLink({
    path: button?.link,
    typeName: buttonData?.navigationDetails?.pageData?.__typename as string,
    config,
    officeInfo,
    officeLocationInfo
  });
  const typeOfOpening = getTypeOfOpening(button.openInNewWindow, button.openInNewTab);
  const handleRenderQuiz = (quiz: string) => {
    if (quiz !== 'ToothReplacement') {
      return null;
    }
    setState({
      ...state,
      showQuiz: true
    });
    if (!quizDetails?.isFormBuilder) {
      quizGA4DataLayerPush(config.name, 'start', 'N/A', quizDetails?.quizFormData as TFormData);
    }
  };
  const handleCloseQuiz = () => {
    setState({
      ...state,
      showQuiz: false
    });
  };
  const handleButtonClick: MouseEventHandler<HTMLButtonElement> = e => {
    if (launchCrestaAgent) {
      e.preventDefault();
      triggerCrestaChat();
      return;
    }
    if ((popoverContentCollection?.items ?? []).length > 0) {
      e.preventDefault();
      return;
    }
    if (quizDetails?.quizType) {
      handleRenderQuiz(quizDetails.quizType);
      return;
    }
    if (button?.externalUrl) {
      if (typeOfOpening !== 'same_tab') {
        e.preventDefault();
        openLinkIn(button?.externalUrl, typeOfOpening);
      }
      return;
    }
    if (buttonData?.navigationDetails?.isInternal && resolvedLink) {
      if (typeOfOpening !== 'same_tab') {
        e.preventDefault();
        openLinkIn(resolvedLink, typeOfOpening);
      }
      return;
    }
    onClick(e);
  };
  const buttonLabelRaw = replaceContentVars({
    text: button?.label,
    officeInfo,
    officeLocationInfo,
    config
  });
  const buttonLabel = normalizeDomainDisplayLink(buttonLabelRaw);
  return <>
			{state.showQuiz ? <Quiz openQuiz={state.showQuiz} handleCloseQuiz={handleCloseQuiz} quizFormData={quizDetails?.quizFormData as TFormData} submitEndpointUrl={quizDetails?.submitEndpointUrl ?? ''} showInDialogOrDrawer={true} quizType={quizDetails?.quizType ?? 'ToothReplacement'} isFormBuilder={quizDetails?.isFormBuilder ?? false} formBuilderConfig={quizDetails?.formBuilderConfig as DynamicFormConfig} /> : null}

			<TAGButton href={quizDetails?.quizType || (popoverContentCollection?.items ?? []).length > 0 || typeOfOpening !== 'same_tab' ? undefined : button?.externalUrl ?? resolvedLink ?? undefined} size={resolveContentfulButtonSize(button?.size as ContentfulButtonSize)} variant={resolveContentfulButtonVariant(button?.variant as ContentfulButtonVariant, defaultVariant)} startIcon={button?.iconPosition === 'start' && button?.icon ? <TAGSvgIcon icon={button.icon} size={buttonIconSize(button.iconSize || 'Medium')} dataTestId={`${dataTestId}_start_icon_${button.id}`} /> : null} endIcon={button?.iconPosition === 'end' && button?.icon ? <TAGSvgIcon icon={button.icon} size={buttonIconSize(button.iconSize || 'Medium')} dataTestId={`${dataTestId}_end_icon_${button.id}`} /> : null} aria-label={ariaLabel} analyticsContext={buttonData?.analyticsContext} disabled={!!launchCrestaAgent && !crestaReady} onClick={handleButtonClick} sx={{
      ...buttonStyles,
      ...docProps,
      px: button?.icon ? '0.875rem' : '1.5rem',
      width: fullWidth ? '100%' : 'auto'
    }} dataTestId={`${dataTestId}_${button?.id ?? 'defaultId'}`} data-sentry-element="TAGButton" data-sentry-source-file="ContentfulButton.tsx">
				{buttonLabel}
			</TAGButton>
		</>;
};
export default ContentfulButton;